import { WebhookDispatch } from "@/@types/WebhookDispatch";
import Badge from "@/components/shared/Badge/Badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { IntegrationsService } from "@/services/integrations.service";
import useModalStore from "@/store/useModalStore";
import { withoutUnpublishedSubscriptionStatuses } from "@/utils/integrationSubscription";
import { getIntegrationWithIcon } from "@/utils/integrations";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Check,
  Clock,
  Copy,
  ExternalLink,
  Key,
  Loader2,
  RotateCcw,
  Send,
  Server,
  Shield,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const WEBHOOK_DETAILS_MODAL = "webhookDetailsModal";

interface WebhookDetailsModalProps {
  webhook: WebhookDispatch | null;
  onRetrySuccess?: () => void;
}

export default function WebhookDetailsModal({
  webhook,
  onRetrySuccess,
}: WebhookDetailsModalProps) {
  const { t } = useTranslation(["integration", "common"]);
  const { modals, closeModal } = useModalStore();
  const queryClient = useQueryClient();

  const [copiedPayload, setCopiedPayload] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const isOpen = modals[WEBHOOK_DETAILS_MODAL] || false;

  const { mutate: retryWebhook, isPending: isRetrying } = useMutation({
    mutationFn: (id: string) => IntegrationsService.retrySendingWebhook(id),
    onSuccess: async () => {
      // Delay de 2 segundos para aguardar o resultado do webhook
      await new Promise((resolve) => setTimeout(resolve, 2000));

      await queryClient.invalidateQueries({
        queryKey: ["webhooks-history"],
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: ["getSellInfo"],
        exact: false,
      });
      toast({
        description: t("retry_success"),
        variant: "success",
      });
      onRetrySuccess?.();
    },
    onError: (error: any) => {
      toast({
        description: error?.message || t("common:data_submission_error"),
        variant: "destructive",
      });
    },
  });

  const handleCopyPayload = async () => {
    if (!webhook?.payload) return;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(webhook.payload, null, 2),
      );
      setCopiedPayload(true);
      setTimeout(() => setCopiedPayload(false), 2000);
      toast({ description: t("copied"), variant: "success" });
    } catch {
      toast({ description: t("common:errorCopying"), variant: "destructive" });
    }
  };

  const handleCopyResponse = async () => {
    if (!webhook?.response) return;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(webhook.response, null, 2),
      );
      setCopiedResponse(true);
      setTimeout(() => setCopiedResponse(false), 2000);
      toast({ description: t("copied"), variant: "success" });
    } catch {
      toast({ description: t("common:errorCopying"), variant: "destructive" });
    }
  };

  const handleCopyToken = async (token: string, type: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(type);
      setTimeout(() => setCopiedToken(null), 2000);
      toast({ description: t("copied"), variant: "success" });
    } catch {
      toast({ description: t("common:errorCopying"), variant: "destructive" });
    }
  };

  const handleClose = () => {
    closeModal(WEBHOOK_DETAILS_MODAL);
  };

  const handleRetry = () => {
    if (webhook?.id) {
      retryWebhook(webhook.id);
    }
  };

  const getHttpStatusColor = (status: number | null) => {
    if (!status) return "text-muted-foreground";
    if (status >= 200 && status < 300) return "text-[#A9D342]";
    if (status >= 400 && status < 500) return "text-[#FACC15]";
    if (status >= 500) return "text-[#F87171]";
    return "text-muted-foreground";
  };

  const maskToken = (token: string) => {
    if (token.length <= 12) return token;
    return `${token.slice(0, 6)}...${token.slice(-6)}`;
  };

  if (!webhook) return null;

  const integration = webhook.integration;
  const hasPostbackUrl = integration?.postback_url;
  const hasAccessToken = integration?.access_token;
  const hasSecretToken = integration?.secret_token;
  const visibleStatuses = withoutUnpublishedSubscriptionStatuses(
    integration?.active_statuses,
  );
  const hasActiveStatuses = visibleStatuses.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="flex flex-col w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-hidden p-0 min-w-0 gap-0">
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col max-h-[90vh] w-full min-w-0 overflow-hidden"
          >
            <DialogHeader className="p-3 sm:p-6 pb-2 sm:pb-4 border-b border-colorBorder shrink-0 min-w-0">
              <div className="flex items-center justify-between pr-6 sm:pr-8 gap-2 min-w-0">
                <DialogTitle className="text-base sm:text-xl font-semibold truncate min-w-0">
                  {t("webhook_details")}
                </DialogTitle>
                <div className="shrink-0">
                  <Badge status={webhook.status} />
                </div>
              </div>
            </DialogHeader>

            <div className="overflow-y-auto overflow-x-hidden flex-1 p-4 sm:p-6 space-y-4 sm:space-y-6 min-w-0">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 min-w-0"
              >
                <div className="bg-zinc-900/50 rounded-lg p-2.5 sm:p-3 border border-colorBorder min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground text-[10px] sm:text-xs mb-1">
                    <Send size={10} className="sm:w-3 sm:h-3 shrink-0" />
                    <span className="truncate">{t("event")}</span>
                  </div>
                  <p className="text-colorText text-xs sm:text-sm font-medium truncate">
                    {webhook.event}
                  </p>
                </div>

                <div className="bg-zinc-900/50 rounded-lg p-2.5 sm:p-3 border border-colorBorder min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground text-[10px] sm:text-xs mb-1">
                    <Server size={10} className="sm:w-3 sm:h-3 shrink-0" />
                    <span className="truncate">{t("http_status")}</span>
                  </div>
                  <p
                    className={`text-xs sm:text-sm font-bold truncate ${getHttpStatusColor(webhook.http_status)}`}
                  >
                    {webhook.http_status ?? "---"}
                  </p>
                </div>

                <div className="bg-zinc-900/50 rounded-lg p-2.5 sm:p-3 border border-colorBorder min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground text-[10px] sm:text-xs mb-1">
                    <RotateCcw size={10} className="sm:w-3 sm:h-3 shrink-0" />
                    <span className="truncate">{t("attempts")}</span>
                  </div>
                  <p className="text-colorText text-xs sm:text-sm font-medium truncate">
                    {webhook.attempts} / {webhook.max_attempts}
                  </p>
                </div>

                <div className="bg-zinc-900/50 rounded-lg p-2.5 sm:p-3 border border-colorBorder min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground text-[10px] sm:text-xs mb-1">
                    <Clock size={10} className="sm:w-3 sm:h-3 shrink-0" />
                    <span className="truncate">{t("last_attempt")}</span>
                  </div>
                  <p className="text-colorText text-xs sm:text-sm font-medium truncate">
                    {webhook.last_attempt ?? "---"}
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder min-w-0"
              >
                <h3 className="text-xs sm:text-sm font-semibold text-colorText mb-3 flex items-center gap-2 min-w-0">
                  <ExternalLink size={12} className="sm:w-3.5 sm:h-3.5 shrink-0" />
                  <span className="truncate">{t("integration_info")}</span>
                </h3>

                <div className="space-y-3 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    {getIntegrationWithIcon(integration?.integration || "") || (
                      <span className="text-colorText text-sm truncate">
                        {integration?.name}
                      </span>
                    )}
                    {integration?.name &&
                      integration.name !== integration.integration && (
                        <span className="text-xs text-muted-foreground bg-zinc-800 px-2 py-0.5 rounded truncate max-w-full">
                          {integration.name}
                        </span>
                      )}
                  </div>

                  {hasPostbackUrl && (
                    <div className="space-y-1 min-w-0">
                      <span className="text-muted-foreground text-[10px] sm:text-xs flex items-center gap-1">
                        <ExternalLink size={10} />
                        {t("postback_url")}
                      </span>
                      <div className="bg-zinc-950 rounded-lg p-2 sm:p-2.5 border border-zinc-800 min-w-0 overflow-hidden">
                        <p className="text-[10px] sm:text-xs text-zinc-300 break-all font-mono">
                          {integration.postback_url}
                        </p>
                      </div>
                    </div>
                  )}

                  {hasAccessToken && (
                    <div className="space-y-1 min-w-0">
                      <span className="text-muted-foreground text-[10px] sm:text-xs flex items-center gap-1">
                        <Key size={10} />
                        Access Token
                      </span>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="flex-1 min-w-0 bg-zinc-950 rounded-lg p-2 sm:p-2.5 border border-zinc-800 overflow-hidden">
                          <p className="text-[10px] sm:text-xs text-zinc-300 break-all font-mono">
                            {maskToken(integration.access_token!)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleCopyToken(integration.access_token!, "access")
                          }
                          className="h-8 w-8 shrink-0"
                        >
                          {copiedToken === "access" ? (
                            <Check size={14} className="text-[#A9D342]" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {hasSecretToken && (
                    <div className="space-y-1 min-w-0">
                      <span className="text-muted-foreground text-[10px] sm:text-xs flex items-center gap-1">
                        <Shield size={10} />
                        Secret Token
                      </span>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="flex-1 min-w-0 bg-zinc-950 rounded-lg p-2 sm:p-2.5 border border-zinc-800 overflow-hidden">
                          <p className="text-[10px] sm:text-xs text-zinc-300 break-all font-mono">
                            {maskToken(integration.secret_token!)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleCopyToken(integration.secret_token!, "secret")
                          }
                          className="h-8 w-8 shrink-0"
                        >
                          {copiedToken === "secret" ? (
                            <Check size={14} className="text-[#A9D342]" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {hasActiveStatuses && (
                    <div className="space-y-1.5">
                      <span className="text-muted-foreground text-[10px] sm:text-xs flex items-center gap-1">
                        <Zap size={10} />
                        {t("integration:test_webhook.event_type")}
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {visibleStatuses.map((status) => (
                          <span
                            key={status}
                            className={cn(
                              "text-[10px] sm:text-xs px-2 py-0.5 rounded-md",
                              "bg-primary/10 text-primary border border-primary/20",
                            )}
                          >
                            {t(`common:${status}`, { defaultValue: status })}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="min-w-0"
              >
                <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
                  <h3 className="text-xs sm:text-sm font-semibold text-colorText truncate min-w-0">
                    {t("payload")}
                  </h3>
                  {webhook.payload &&
                    Object.keys(webhook.payload).length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyPayload}
                        className="h-7 sm:h-8 px-2 text-[10px] sm:text-xs shrink-0"
                      >
                        {copiedPayload ? (
                          <Check size={12} className="text-[#A9D342]" />
                        ) : (
                          <Copy size={12} />
                        )}
                        <span className="ml-1 hidden sm:inline">
                          {t("copy_payload")}
                        </span>
                        <span className="ml-1 sm:hidden">
                          {t("common:copy")}
                        </span>
                      </Button>
                    )}
                </div>
                <div className="bg-zinc-950 rounded-lg border border-colorBorder overflow-hidden min-w-0 max-w-full">
                  {webhook.payload &&
                  Object.keys(webhook.payload).length > 0 ? (
                    <pre className="p-3 sm:p-4 text-[10px] sm:text-xs text-[#A9D342] overflow-x-auto max-h-40 sm:max-h-48 overflow-y-auto max-w-full whitespace-pre-wrap break-all">
                      <code>{JSON.stringify(webhook.payload, null, 2)}</code>
                    </pre>
                  ) : (
                    <div className="p-3 sm:p-4 flex items-center gap-2 text-muted-foreground text-xs sm:text-sm">
                      <AlertCircle size={14} className="shrink-0" />
                      {t("no_payload")}
                    </div>
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="min-w-0"
              >
                <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
                  <h3 className="text-xs sm:text-sm font-semibold text-colorText truncate min-w-0">
                    {t("response")}
                  </h3>
                  {webhook.response && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyResponse}
                      className="h-7 sm:h-8 px-2 text-[10px] sm:text-xs shrink-0"
                    >
                      {copiedResponse ? (
                        <Check size={12} className="text-[#A9D342]" />
                      ) : (
                        <Copy size={12} />
                      )}
                      <span className="ml-1 hidden sm:inline">
                        {t("copy_response")}
                      </span>
                      <span className="ml-1 sm:hidden">{t("common:copy")}</span>
                    </Button>
                  )}
                </div>
                <div className="bg-zinc-950 rounded-lg border border-colorBorder overflow-hidden min-w-0 max-w-full">
                  {webhook.response ? (
                    <pre className="p-3 sm:p-4 text-[10px] sm:text-xs text-[#60A5FA] overflow-x-auto max-h-40 sm:max-h-48 overflow-y-auto max-w-full whitespace-pre-wrap break-all">
                      <code>{JSON.stringify(webhook.response, null, 2)}</code>
                    </pre>
                  ) : (
                    <div className="p-3 sm:p-4 flex items-center gap-2 text-muted-foreground text-xs sm:text-sm">
                      <AlertCircle size={14} className="shrink-0" />
                      {t("no_response")}
                    </div>
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm min-w-0"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0">
                  <span className="text-muted-foreground shrink-0">
                    {t("send_date")}:
                  </span>
                  <span className="text-colorText truncate">
                    {webhook.sent_at_formatted}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0">
                  <span className="text-muted-foreground shrink-0">
                    {t("common:created_at")}:
                  </span>
                  <span className="text-colorText truncate">
                    {webhook.created_at_formatted}
                  </span>
                </div>
              </motion.div>
            </div>

            {webhook.available_to_retry && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="p-3 sm:p-6 pt-3 sm:pt-4 border-t border-colorBorder bg-background shrink-0"
              >
                <Button
                  onClick={handleRetry}
                  disabled={isRetrying || webhook.attempts >= 3}
                  className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                >
                  {isRetrying ? (
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  ) : (
                    <RotateCcw className="mr-2 h-4 w-4" />
                  )}
                  {isRetrying
                    ? t("common:processing")
                    : webhook.attempts >= 3
                      ? t("max_attempts_reached", {
                          defaultValue: "Limite de tentativas atingido",
                        })
                      : t("retry")}
                </Button>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
